import cx from 'classnames';
import _ from 'lodash';
import React, { useCallback, useEffect, useRef, useState, } from 'react';
import { cloneChildren, useKeyPressNavigation } from '../_utils';
import styles from './menu.module.scss';
import { MenuGroup } from './menu-group';
import { MenuItem } from './menu-item';
import { MenuVariant } from './types';
export const Menu = ({ id, children, className, isSubMenu = false, show = false, disableKeyboardNavigation = false, variant = MenuVariant.DEFAULT, onMenuItemKeyPressCB = _.noop, disableTabIndex = false, menuTheme = undefined, ...restProps }) => {
    const menu = useRef(null);
    const [menuStyle, setMenuStyle] = useState();
    const isSideNavVariant = [
        MenuVariant.SIDE_NAV,
        MenuVariant.MODAL_SIDE_NAV,
    ].includes(variant);
    const actionMenuClasses = cx(styles.actionMenu, styles.withOutIcons, className, {
        [styles.sideNavVariant]: isSideNavVariant,
    });
    const setSubMenuStyle = useCallback(() => {
        const { innerWidth } = window;
        let subMenuStyle = cx(styles.cascadingMenu);
        // To calculate the current bounding box of the subMenu Element
        const rect = menu.current && menu.current.getBoundingClientRect();
        subMenuStyle =
            rect && rect.right + rect.width < innerWidth
                ? cx(subMenuStyle, styles.openRight)
                : cx(subMenuStyle, styles.openLeft);
        setMenuStyle(subMenuStyle);
    }, [menu]);
    useEffect(() => {
        if (isSubMenu && menu.current) {
            setSubMenuStyle();
        }
    }, [isSubMenu, menu, setSubMenuStyle, show]);
    const getSortedMenuItems = () => {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o;
        const menuItemClass = cx(styles.item);
        const menuGroupWrapperClass = cx(styles.menuGroupWrapper);
        const menuItemDisabledClass = cx(styles.itemDisabled);
        const menuItemList = [];
        // identify only the menu items and ignore the inner child sub menu items
        for (let i = 0; i < ((_b = (_a = menu === null || menu === void 0 ? void 0 : menu.current) === null || _a === void 0 ? void 0 : _a.children) === null || _b === void 0 ? void 0 : _b.length); i++) {
            const childElement = (_c = menu === null || menu === void 0 ? void 0 : menu.current) === null || _c === void 0 ? void 0 : _c.children[i];
            // if the wrapper is there, then it means that the element is part of menu and not inside submenu
            if ((_d = childElement === null || childElement === void 0 ? void 0 : childElement.getAttribute('class')) === null || _d === void 0 ? void 0 : _d.includes(menuGroupWrapperClass)) {
                for (let j = 0; j < ((_e = childElement === null || childElement === void 0 ? void 0 : childElement.children) === null || _e === void 0 ? void 0 : _e.length); j++) {
                    const menuEle = childElement === null || childElement === void 0 ? void 0 : childElement.children[j];
                    if (((_f = menuEle === null || menuEle === void 0 ? void 0 : menuEle.getAttribute('class')) === null || _f === void 0 ? void 0 : _f.includes(menuItemClass)) &&
                        !((_g = menuEle === null || menuEle === void 0 ? void 0 : menuEle.getAttribute('class')) === null || _g === void 0 ? void 0 : _g.includes(menuItemDisabledClass))) {
                        menuItemList.push(menuEle);
                    }
                }
            }
        }
        const menuItemIDs = [];
        for (let i = 0; i < menuItemList.length; i++) {
            if (!_.isEmpty((_h = menuItemList[i]) === null || _h === void 0 ? void 0 : _h.id) &&
                !(((_j = menuItemList[i]) === null || _j === void 0 ? void 0 : _j.id[0]) <= '9' &&
                    ((_k = menuItemList[i]) === null || _k === void 0 ? void 0 : _k.id[0]) >= '0') &&
                !((_m = (_l = menuItemList[i]) === null || _l === void 0 ? void 0 : _l.id) === null || _m === void 0 ? void 0 : _m.includes('/'))) {
                menuItemIDs.push((_o = menuItemList[i]) === null || _o === void 0 ? void 0 : _o.id);
            }
        }
        return menuItemIDs;
    };
    const menuItemIDs = getSortedMenuItems();
    const clickMenuItem = (itemID) => {
        var _a;
        if (!disableKeyboardNavigation) {
            const ele = (_a = menu === null || menu === void 0 ? void 0 : menu.current) === null || _a === void 0 ? void 0 : _a.querySelector(`[id="${itemID}"]`);
            ele === null || ele === void 0 ? void 0 : ele.click();
        }
    };
    const { cursor } = useKeyPressNavigation(document, menuItemIDs, clickMenuItem, show);
    useEffect(() => {
        var _a;
        if (!disableKeyboardNavigation) {
            if (cursor >= 0) {
                const ele = (_a = menu === null || menu === void 0 ? void 0 : menu.current) === null || _a === void 0 ? void 0 : _a.querySelector(`[id="${menuItemIDs[cursor]}"]`);
                ele === null || ele === void 0 ? void 0 : ele.focus();
            }
        }
    }, [cursor, disableKeyboardNavigation, menu, menuItemIDs]);
    const renderChildren = (clsName) => (React.createElement("div", { role: "menuitem", tabIndex: disableTabIndex ? -1 : 0, id: id, ref: menu, className: cx(clsName, {
            [styles[menuTheme]]: menuTheme !== undefined,
        }), "data-testid": id, ...restProps }, React.Children.toArray(children).map((item) => {
        if ((item === null || item === void 0 ? void 0 : item.type) === MenuGroup) {
            return cloneChildren(item, {
                variant,
                onMenuItemKeyPressCB,
            });
        }
        return cloneChildren(item, {
            variant,
        });
    })));
    const renderSubMenu = () => (React.createElement("div", { className: cx(menuStyle, {
            [styles[menuTheme]]: menuTheme !== undefined,
        }), ...restProps, role: "listbox", tabIndex: 0 },
        React.createElement("div", { className: actionMenuClasses }, isSubMenu && renderChildren(styles.itemContainer))));
    return (React.createElement(React.Fragment, null, show &&
        (isSubMenu
            ? renderSubMenu()
            : renderChildren(actionMenuClasses))));
};
Menu.Item = MenuItem;
Menu.Group = MenuGroup;
//# sourceMappingURL=menu.js.map